/**
 * This module provides a utility class for managing a unique set of objects.
 */

/**
 * UniqueSet class
 * This class allows you to create a set of unique objects, ensuring that no duplicates are added
 * and providing methods to manipulate the set.
 * @template T - The type of objects stored in the set, which must extend object.
 * * @example
 * const uniqueSet = new UniqueSet<MyObjectType>();
 * uniqueSet.add({ id: 1, name: 'Item 1' });
 * uniqueSet.add({ id: 2, name: 'Item 2' });
 * uniqueSet.add({ id: 1, name: 'Item 1' }); // This will not be added again
 * console.log(uniqueSet.getItems()); // [{ id: 1, name: 'Item 1' }, { id: 2, name: 'Item 2' }]
 * uniqueSet.clear(); // Clears the set
 */
export default class UniqueSet<T extends object = any> {
  private items: T[] = []

  constructor(iterable?: Iterable<T> | null | undefined) {
    if (iterable) {
      Array.from(iterable).forEach(item => {
        this.add(item)
      })
    }
  }

  add(item: T) {
    if (!this.has(item)) {
      this.items.push(item)
    }
  }

  addAll(
    iterable?: Iterable<T> | null | undefined,
    on: 'start' | 'end' = 'end'
  ) {
    if (iterable) {
      let listToAdd: T[] = Array.from(iterable)
      if (on == 'start') {
        listToAdd = listToAdd.concat(this.items)
        this.items = []
      }
      Array.from(listToAdd).forEach(item => {
        this.add(item)
      })
    }
  }

  has(value: object) {
    return this.items.some(item => {
      if ('equals' in item) {
        const compare = item.equals as (value: object) => boolean
        return compare(value)
      }
      return item === value
    })
  }

  private get asArray(): T[] {
    return Array.from(this.items)
  }

  get first(): T {
    return this.items[0]
  }

  getItems(): T[] {
    return Array.from(this.items)
  }

  reverse(): T[] {
    return this.asArray.reverse()
  }

  get size(): number {
    return this.items.length
  }

  clear(): void {
    this.items.splice(0, this.size)
  }
}
